import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const supportedLanguages = ['id', 'en'] as const;
export type AppLanguage = (typeof supportedLanguages)[number];

const id = {
  translation: {
    language: {
      label: 'Bahasa',
      id: 'Bahasa Indonesia',
      en: 'English',
      shortId: 'ID',
      shortEn: 'EN',
    },
    common: {
      home: 'Beranda',
      explore: 'Jelajahi',
      learnSolo: 'Mengenal Solo',
      districts: 'Kawasan',
      travelGuide: 'Panduan Wisata',
      culinary: 'Rasa Sala',
      stories: 'Cerita Kota',
      today: 'Agenda',
      play: 'Mainkan',
      supportLocal: 'Dukung Lokal',
      passport: 'Paspor',
      map3d: 'Peta 3D',
      view360: 'Solo 360°',
      search: 'Cari',
      close: 'Tutup',
      open: 'Buka',
      viewAll: 'Lihat semua',
      viewDetails: 'Lihat detail',
      backHome: 'Kembali ke beranda AmertaSala',
      mainNavigation: 'Navigasi utama',
      cityNavigation: 'Navigasi city brand',
      searchCity: 'Cari informasi kota',
      openMenu: 'Buka menu',
      closeMenu: 'Tutup menu',
      exploreSolo: 'Jelajahi Solo',
      loading: 'Memuat…',
      loadError: 'Konten belum dapat dimuat.',
      demoData: 'Data demonstrasi MVP',
      all: 'Semua',
      saved: 'Tersimpan',
      remove: 'Hapus',
      retry: 'Ulangi',
      level: 'Level {{level}} · {{xp}} XP',
    },
    header: {
      map: 'Peta 3D',
    },
    footer: {
      intro: 'Gerbang digital Kota Solo untuk memahami kota, merencanakan perjalanan, mendengar cerita warga, dan mengalami warisan melalui teknologi interaktif.',
      exploreMap: 'Jelajahi peta',
      playBatik: 'Mainkan batik',
      explore: 'Jelajahi',
      interact: 'Berinteraksi',
      brandPlatform: 'Brand Platform',
      motifGame: 'Game Motif',
      digitalPassport: 'Paspor Digital',
      methodology: 'Metodologi',
      prototype: 'Prototype Nusantara Digital City · data program, agenda, dan dampak bersifat demonstrasi untuk kebutuhan MVP.',
    },
    landing: {
      eyebrow: 'AMERTASALA · WHERE HERITAGE LIVES FORWARD',
      heroTitle1: 'Solo, kota tempat',
      heroTitle2: 'warisan terus hidup.',
      heroTitle3: '',
      heroBody: 'Masuki Solo melalui cerita, rasa, kawasan, dan pengalaman digital yang dirancang untuk membantu wisatawan memahami kota—bukan sekadar melihatnya.',
      heroAlt: 'Panorama Solo saat golden hour',
      mapAlt: 'Pratinjau peta interaktif Solo',
      philosophyAlt: 'Kain batik menghadap panorama Solo',
      culinaryAlt: 'Hidangan khas Solo',
      makerAlt: 'Pembatik Solo',
      storyAlt: 'Potret warga Solo',
      srawungAlt: 'Interaksi hangat antara warga dan pengunjung di Solo',
      searchPlaceholder: 'Cari tempat, rasa, cerita, atau pengalaman di Solo...',
      searchAria: 'Cari di Solo',
      find: 'Temukan',
      quoteBefore: '“Datang untuk melihat. ',
      quoteEmphasis: 'Srawung',
      quoteAfter: ' untuk memahami.”',
      interactiveMap: 'Peta interaktif',
      openMap: 'Buka',
      mapTitle: 'Peta yang terasa nyata, bukan sekadar pin.',
      mapBody: 'Eksplorasi landmark, kawasan, dan rute budaya melalui pengalaman peta 3D yang imersif.',
      upcoming: 'Agenda terdekat',
      philosophyEyebrow: 'Filosofi AmertaSala',
      philosophyTitle: 'Bukan katalog. Bukan dashboard. Tetapi ruang untuk merasakan jiwa kota.',
      philosophyBody: 'AmertaSala menempatkan Solo sebagai pengalaman yang hidup. Informasi kota tetap lengkap, tetapi disampaikan dengan ritme visual yang lebih tenang, humanis, dan berlapis.',
      philosophy1: 'Portal informasi kota yang jelas',
      philosophy2: 'Panduan wisata digital yang membantu',
      philosophy3: 'Pengalaman budaya yang lebih terasa',
      responsible: 'Cara Disrawungi',
      districtEyebrow: 'Jelajahi kawasan',
      districtTitle: 'Pilih satu kawasan. Rasakan satu karakter Solo.',
      districtBody: 'Setiap pilihan membuka visual, cerita, dan ritme kawasan yang berbeda.',
      allDistricts: 'Lihat semua kawasan',
      viewMap: 'Lihat di peta',
      districtStory: 'Baca kisah kawasan',
      journeyEyebrow: 'Panduan wisata digital',
      journeyTitle: 'Rute yang membantu, tetapi tetap memberi ruang untuk eksplorasi.',
      journeyBody: 'Pilih perjalanan dan lihat alurnya terbentuk secara visual.',
      allRoutes: 'Lihat semua rute',
      addJourney: 'Masukkan ke Perjalanan Saya',
      routeDetails: 'Lihat detail rute',
      editorialEyebrow: 'Cerita pilihan',
      editorialTitle: 'Konten editorial yang muncul satu per satu saat ditemukan.',
      culinaryFallbackTitle: 'Rasa yang menjaga tradisi',
      culinaryFallbackBody: 'Cerita kuliner yang disajikan lebih hangat dan berkarakter.',
      makerFallbackTitle: 'Pembuat yang menjaga karya',
      makerFallbackBody: 'Temui pembuat karya lokal dan pahami proses di baliknya.',
      storyFallbackTitle: 'Cerita yang hidup dari warga kota',
      storyFallbackBody: 'Dengarkan cerita dari mereka yang menjaga denyut Solo setiap hari.',
      openCulinary: 'Buka panduan kuliner',
      meetMaker: 'Kenal pembuatnya',
      listenStory: 'Dengarkan cerita',
      responsibleTitle: 'Interaktif, tetapi tetap hangat dan manusiawi.',
      responsibleBody: 'Motion dipakai untuk mengarahkan perhatian, bukan sekadar hiasan. Pengunjung tetap bisa membaca dengan tenang dan memahami setiap pesan.',
      featureCity: 'Portal informasi kota',
      featureResponsible: 'Wisata yang lebih bertanggung jawab',
      featureCalm: 'Pengalaman yang lebih tenang dan jelas',
      learnGuide: 'Pelajari panduan',
      experienceEyebrow: 'Pengalaman digital',
      experienceTitle: 'Tiga pintu utama untuk mulai menjelajah.',
      mapCardBody: 'Jelajahi landmark, kawasan, dan lapisan kota.',
      batikTrace: 'Batik Trace',
      batikBody: 'Mainkan motif dan pahami filosofi Solo.',
      passportCollection: 'Paspor & Koleksi',
      passportBody: 'Simpan jejak, cap, dan cerita perjalananmu.',
      destinations: '{{count}} destinasi',
      finalEyebrow: 'Siap menemukan Solo versimu?',
      finalTitle: 'Solo menanti untuk disrawungi.',
      finalBody: 'Jelajahi kota, mainkan warisan, dan bawa pulang cerita yang lebih bermakna.',
      planJourney: 'Rencanakan perjalanan',
      enteringMap: 'Memasuki Peta Solo 3D…',
      startExplore: 'Mulai Jelajah',
      quickBatik: 'Batik',
      quickCulinary: 'Kuliner',
      quickPalace: 'Keraton',
      quickFamily: 'Keluarga',
      quickAgenda: 'Agenda',
    },
    view360: {
      eyebrow: 'Jelajah virtual kota',
      title: 'Solo dalam 360°',
      title1: 'Masuki jalan, ruang,',
      title2: 'dan sudut Kota Solo.',
      intro: 'Jelajahi 40 lokasi Solo dan Solo Raya melalui panorama interaktif, dengarkan ceritanya, lalu lanjutkan ke peta atau itinerary.',
      locations: 'lokasi',
      categories: 'kategori',
      immersive: 'pengalaman imersif',
      destinations: 'Destinasi pilihan',
      sidebarTitle: 'Keliling Solo dari layar.',
      sidebarBody: 'Pilih dari {{count}} destinasi, lalu klik dan geser panorama untuk melihat suasana sekelilingnya.',
      searchPlaceholder: 'Cari tempat, kawasan, atau kategori…',
      choosePlace: 'Pilih tempat',
      noResults: 'Tempat tidak ditemukan',
      noResultsBody: 'Coba gunakan kata kunci atau kategori lain.',
      interactivePanorama: 'Panorama interaktif',
      reload: 'Muat ulang',
      fullscreen: 'Layar penuh',
      googleMaps: 'Google Maps',
      preparing: 'Menyiapkan panorama {{name}}',
      wait: 'Mohon tunggu beberapa detik…',
      frameTitle: 'Street View 360 derajat {{name}}',
      dragHint: 'Klik dan geser untuk melihat 360°',
      previous: 'Lokasi sebelumnya',
      next: 'Lokasi berikutnya',
      bestTime: 'Waktu terbaik',
      position: 'Urutan',
      whyInteresting: 'Kenapa menarik?',
      openInMap: 'Lihat di Peta 3D',
      continueJourney: 'Lanjutkan perjalanan nyata',
      continueJourneyBody: 'Simpan lokasi ini atau buka rute wisata yang sesuai.',
      audioTitle: 'Jelajah sambil mendengarkan',
      audioBody: 'Narasi browser mengikuti bahasa aktif. Pilih ID atau EN, lalu dengarkan cerita lokasi tanpa harus terus membaca layar.',
      narrationIntro: 'Selamat datang di Solo dalam tiga ratus enam puluh derajat. Pilih lokasi, gerakkan panorama, dan dengarkan cerita singkat untuk mengenal suasana kota sebelum berkunjung secara langsung.',
      loading: 'Memuat Jelajah Solo 360°…',
      unavailable: 'Data panorama belum dapat dimuat.',
      landingEyebrow: 'Solo dalam 360°',
      landingTitle: 'Melangkah ke dalam Solo, sebelum benar-benar tiba.',
      landingBody: 'Masuki kawasan heritage, pasar, kampung batik, ruang publik, dan simpul transportasi melalui 40 panorama interaktif.',
      landingCta: 'Jelajahi Solo 360°',
      landingHint: 'Geser untuk melihat sekitar',
      available: '{{count}} lokasi tersedia',
      mapButton: 'Lihat 360°',
      itineraryButton: 'Lihat 360°',
    },
    profile: {
      loading: 'Memuat profil kota…',
      error: 'Profil kota belum dapat dimuat.',
      eyebrow: 'Mengenal Solo',
      title1: 'Solo, kota budaya',
      title2: 'yang terus bergerak.',
      exploreMap: 'Jelajahi peta 3D',
      planVisit: 'Rencanakan kunjungan',
      glance: 'Solo dalam sekilas',
      glanceTitle1: 'Informasi kota yang ringkas,',
      glanceTitle2: 'jelas, dan kontekstual.',
      potential: 'Potensi daerah',
      potentialTitle1: 'Kekuatan yang membuat Solo',
      potentialTitle2: 'berdaya saing.',
      potentialBody: 'Setiap sektor terhubung dengan kawasan, pelaku lokal, pengalaman wisata, dan cerita kota.',
      timeline: 'Garis waktu kota',
      timelineTitle1: 'Warisan tidak muncul sekaligus.',
      timelineTitle2: 'Ia tumbuh lintas generasi.',
      ctaTitle: 'Temukan informasi dalam konteks lokasi.',
      ctaBody: 'Buka peta 3D untuk melihat landmark, kawasan, cerita, etika kunjungan, dan quest.',
      enterMap: 'Masuk ke peta',
      panoramaAlt: 'Panorama Kota Solo',
    },
    districts: {
      error: 'Gagal memuat kawasan.',
      eyebrow: 'Explore by district',
      title1: 'Lima kawasan.',
      title2: 'Lima cara membaca Solo.',
      intro: 'Alih-alih menampilkan Solo sebagai daftar objek, AmertaSala memperkenalkan karakter, ritme, dan manusia yang membentuk setiap kawasan.',
      allMap: 'Lihat semuanya di peta 3D',
      etiquette: 'Cara disrawungi',
      openMap: 'Buka kawasan di peta',
      layers: 'Satu kota, banyak lapisan',
      cta1: 'Temukan kawasan yang paling',
      cta2: 'sesuai rasa ingin tahumu.',
      startMap: 'Mulai dari peta Solo',
    },
    itineraries: {
      eyebrow: 'Panduan wisata digital',
      title1: 'Bukan hanya ke mana.',
      title2: 'Tapi bagaimana menjalaninya.',
      intro: 'Pilih perjalanan berdasarkan waktu, minat, gaya wisata, dampak lokal, dan tingkat kenyamanan berjalan kaki.',
      heroAlt: 'Perjalanan budaya di Solo',
      all: 'Semua',
      themed: 'Perjalanan tematik',
      themedTitle1: 'Rute siap dimainkan',
      themedTitle2: 'dan disesuaikan.',
      culture: 'Budaya',
      local: 'Lokal',
      walkability: 'Jalan kaki',
      useRoute: 'Gunakan rute ini',
      myJourney: 'Perjalanan saya',
      board1: 'Papan perjalanan',
      board2: 'yang tersimpan lokal.',
      addFromMap: 'Tambah dari peta',
      empty: 'Belum ada lokasi. Tambahkan dari peta atau gunakan salah satu rute.',
      summary: 'Ringkasan perjalanan',
      estimate: 'Estimasi {{hours}} jam',
      locations: '{{count}} lokasi',
      storedDevice: 'Tersimpan di perangkat',
      disclaimer: 'Urutan dan estimasi pada MVP bersifat simulasi.',
      allLocations: 'Lihat seluruh lokasi',
      remove: 'Hapus {{name}}',
    },
    culinary: {
      eyebrow: 'Rasa Sala',
      title1: 'Kota dapat dikenali',
      title2: 'melalui rasanya.',
      intro: 'Temukan hidangan, jajanan, minuman, pasar, dan cerita manusia di balik budaya makan Solo.',
      heroAlt: 'Kuliner dan kehidupan Pasar Gede',
      nightAlt: 'Kuliner malam Kota Solo',
      addJourney: 'Masukkan rasa ke perjalanan',
      guide: 'Panduan kuliner digital',
      guideTitle1: 'Pilih berdasarkan waktu,',
      guideTitle2: 'suasana, dan cerita.',
      searchPlaceholder: 'Cari makanan, kawasan, atau suasana…',
      vegetarian: 'Vegetarian',
      loading: 'Memuat Rasa Sala…',
      all: 'Semua',
      noResults: 'Belum ada rasa yang cocok dengan pilihanmu.',
      halal: 'Halal',
      saveTaste: 'Simpan rasa',
      nightEyebrow: 'Solo setelah senja',
      nightTitle1: 'Ketika lampu menyala,',
      nightTitle2: 'rasa kota berubah.',
      nightBody: 'Warung, wedang, sajian panggang, dan obrolan malam menjadi bagian penting dari pengalaman Solo.',
      nightRoute: 'Lihat rute malam',
    },
    stories: {
      error: 'Gagal memuat cerita kota.',
      eyebrow: 'Suara dari Sala',
      title1: 'Kota tidak hanya dibangun oleh bangunan.',
      title2: 'Kota dibangun oleh suara.',
      editorial: 'Editorial city brand',
      people1: 'Orang-orang yang membuat',
      people2: 'Solo terus hidup.',
      pause: 'Jeda cerita',
      listen: 'Dengarkan cerita',
      playAria: 'Putar cerita {{name}}',
      introBody: 'Dalam versi produksi, bagian ini menjadi ruang wawancara, rekaman suara, foto dokumenter, dan arsip cerita yang dikurasi bersama warga.',
      archive: 'City archive',
      cta: 'Satu cerita membuka cara baru melihat sebuah tempat.',
      findDistrict: 'Temukan kawasan ceritanya',
    },
    makers: {
      error: 'Gagal memuat Made in Sala.',
      eyebrow: 'Made in Sala',
      title1: 'Bukan sekadar barang.',
      title2: 'Ada tangan, waktu, dan pengetahuan.',
      intro: 'Made in Sala menjadikan produk lokal sebagai media branding kota—dengan cerita pembuat, proses, cara merawat, dan dampak sosial yang dapat dipahami.',
      heroAlt: 'Koleksi karya batik dan benda kriya buatan pembuat lokal Solo',
      meet: 'Temui pembuatnya',
      transparency: 'Transparansi produk',
      transparencyTitle: 'Setiap karya menjawab empat pertanyaan.',
      who: 'Siapa pembuatnya?',
      whoBody: 'Nama dan komunitas di balik produk.',
      how: 'Bagaimana dibuat?',
      howBody: 'Teknik, material, dan waktu pengerjaan.',
      benefit: 'Siapa yang mendapat manfaat?',
      benefitBody: 'Hubungan produk dengan ekonomi lokal.',
      care: 'Bagaimana dirawat?',
      careBody: 'Agar produk bertahan dan tidak cepat menjadi limbah.',
      since: '{{district}} · sejak {{since}}',
      process: 'Proses',
      duration: 'Durasi',
      careLabel: 'Cara merawat',
    },
    responsible: {
      eyebrow: 'Responsible travel, the Solo way',
      title1: 'Datang untuk melihat.',
      title2: 'Srawung untuk memahami.',
      intro: 'Cara Disrawungi adalah pedoman sederhana agar perjalanan menghormati budaya, ruang hidup warga, dan manfaat ekonomi lokal.',
      heroAlt: 'Wisatawan berinteraksi dengan pedagang lokal di Pasar Gede secara hormat',
      play: 'Belajar melalui permainan',
      principles: '6 prinsip',
      guest: 'Tamu Srawung',
      phase: 'Sebelum, saat, dan setelah berkunjung',
      phaseTitle1: 'Etika tidak harus terasa seperti aturan.',
      phaseTitle2: 'Ia bisa menjadi bagian dari pengalaman.',
      simulation: 'Micro simulation',
      simulationTitle1: 'Pilihan kecil,',
      simulationTitle2: 'pengalaman yang berbeda.',
      quest: 'Temukan quest di peta',
      quick: 'Pilihan cepat',
      quickTitle: 'Datang, memotret, lalu pergi.',
      knowledge: 'Pengetahuan',
      localBenefit: 'Manfaat lokal',
      quickBody: 'Efisien, tetapi pengalaman berhenti pada visual.',
      srawung: 'Pilihan Srawung',
      srawungTitle: 'Meminta izin, bertanya, dan mendukung karya.',
      simulatorBody: 'Dalam produk lengkap, pengguna menghadapi situasi pendek—memotret pedagang, membeli batik, memasuki ruang budaya—lalu melihat dampak pilihannya tanpa dihakimi.',
      principlesList: {
        listenTitle: 'Dengarkan sebelum menilai',
        listenBody: 'Tanyakan cerita, proses, dan konteks sebelum menganggap sebuah tempat hanya sebagai objek wisata.',
        photoTitle: 'Minta izin sebelum memotret',
        photoBody: 'Rumah, pasar, bengkel, dan ruang ibadah adalah ruang hidup—bukan studio foto tanpa batas.',
        spendTitle: 'Biarkan belanjamu tinggal lebih lama',
        spendBody: 'Pilih pengalaman, produk, dan layanan yang memberi manfaat langsung kepada pelaku lokal.',
        lightTitle: 'Bergerak lebih ringan',
        lightBody: 'Kurangi sampah sekali pakai, gunakan air dengan sadar, dan pilih perjalanan jarak dekat yang lebih ringan.',
        srawungTitle: 'Srawung, bukan sekadar singgah',
        srawungBody: 'Berikan waktu untuk berinteraksi dan memahami ritme masyarakat yang kamu kunjungi.',
        respectTitle: 'Hormati aturan budaya',
        respectBody: 'Ikuti aturan pakaian, akses, fotografi, dan perilaku pada ruang adat maupun religius.',
      },
      srawungBody: 'Lebih lambat, tetapi meninggalkan hubungan dan pemahaman.',
    },
    today: {
      error: 'Gagal memuat agenda.',
      intro: 'Agenda kota menghubungkan wisatawan dan warga dengan pertunjukan, workshop, jelajah kawasan, kegiatan komunitas, serta ruang kreatif.',
      openEvent: 'Buka {{title}}',
      eyebrow: 'Solo Today',
      title1: 'Warisan bukan hanya masa lalu.',
      title2: 'Ia punya jadwal hari ini.',
      all: 'Semua',
      music: 'Musik',
      textile: 'Wastra',
      food: 'Kuliner',
      history: 'Sejarah',
      environment: 'Lingkungan',
      demo: 'Demo data',
      ctaTitle: 'Agenda akan menjadi alasan untuk kembali ke AmertaSala.',
      ctaBody: 'Dalam implementasi nyata, jadwal perlu terhubung ke sumber resmi dan diperbarui secara teratur.',
      venueMap: 'Jelajahi venue di peta',
    },
    search: {
      eyebrow: 'Pencarian kota',
      title1: 'Cari tempat, rasa,',
      title2: 'cerita, atau agenda.',
      placeholder: 'Contoh: batik, kuliner malam, ruang hijau…',
      button: 'Cari',
      results: 'Semua hasil',
      map: 'Buka peta 3D',
      details: 'Lihat detail',
      noResults: 'Belum ada hasil yang cocok.',
      count: '{{count}} hasil ditemukan · data demonstrasi MVP',
      destination: 'Destinasi',
      district: 'Kawasan',
      culinary: 'Kuliner',
      maker: 'Made in Sala',
      event: 'Agenda',
    },
    collection: {
      eyebrow: 'Paspor digital',
      title: 'Semua jejak, motif, dan cerita yang kamu bawa pulang.',
      republic: 'Republik Cerita Sala',
      explorerName: 'Nama penjelajah',
      guest: 'Tamu Srawung',
      connector: 'Level {{level}} · Penghubung Cerita',
      current: 'Jejakmu saat ini',
      currentBody: 'Perjalanan tumbuh dari hal kecil yang dilakukan dengan sadar.',
      landmarks: 'landmark',
      motifs: 'motif',
      quests: 'quest',
      continue: 'Lanjutkan tantangan',
      gallery: 'Galeri digital',
      learned: 'Motif yang telah kamu pelajari',
      achievements: 'Pencapaian',
      badges: 'Cap & Lencana',
      first: 'Penjelajah Pertama',
      firstBody: 'Membuka peta Solo 3D',
      five: 'Langkah Lima',
      fiveBody: 'Jelajahi 5 landmark',
      motifKeeper: 'Penjaga Motif',
      motifKeeperBody: 'Raih skor motif 85',
      srawungGuest: 'Tamu Srawung',
      srawungGuestBody: 'Selesaikan 3 quest',
      savedLater: 'Disimpan untuk nanti',
      chosen: 'Landmark pilihanmu',
      openMap: 'Buka peta',
      viewMap: 'Lihat di peta',
      empty: 'Simpan landmark dari peta untuk membuat daftar perjalanan.',
      unlockHint: 'Selesaikan tantangan untuk membuka filosofi motif.',
      bestScore: 'Skor terbaik {{score}}',
      locked: 'Terkunci',
    },
    leaderboard: {
      loading: 'Menghitung jejak para penjelajah…',
      eyebrow: 'AmertaSala League',
      intro: 'Kompetisi yang merayakan belajar, bukan sekadar menang.',
      season: 'Musim Jejak Wastra',
      heroBody: 'Peringkat dihitung dari eksplorasi landmark, quest budaya, dan ketelitian menggambar motif. Data pada MVP ini bersifat simulasi.',
      myJourney: 'Perjalananmu',
      collectionMotif: 'Motif Koleksi',
      noMotif: 'Belum memilih',
      days: '{{count}} hari',
      title1: 'Siapa yang paling tekun',
      title2: 'menjaga cerita?',
      ends: 'Musim berakhir',
      time: '04 hari 18 jam',
      reward: 'Hadiah digital: Cap Laweyan Emas',
      weekly: 'Peringkat mingguan',
      keepers: 'Penjaga Motif & Cerita',
      global: 'Global',
      weeklyFilter: 'Mingguan',
      solo: 'Solo',
      rank: 'Peringkat',
      explorer: 'Penjelajah',
      motif: 'Motif',
      streak: 'Streak',
      score: 'Skor',
      healthy: 'Leaderboard yang sehat',
      healthyBody: 'Tidak ada hadiah uang atau ranking permanen. Peringkat direset berkala agar pemain baru tetap punya kesempatan dan fokus utama tetap pada pembelajaran budaya.',
      reset: 'Reset demo',
    },
    game: {
      loading: 'Menyiapkan kain digital…',
      eyebrow: 'Batik Trace Challenge',
      intro: 'Menggambar untuk memahami, bukan sekadar menyalin.',
      backMap: 'Kembali ke peta',
      collection: 'Koleksi motif',
      choose: 'Pilih garis yang ingin kamu pelajari.',
      chooseBody: 'Setiap pola memiliki ritme dan filosofi. Ikuti jalurnya perlahan seperti gerak canting.',
      how: 'Cara bermain & filosofi',
      notPerfect: 'Bukan mencari tangan sempurna.',
      explanation: 'Permainan ini mengajak pengguna mengalami ketelitian, pengulangan, dan kesabaran yang menjadi bagian proses membatik.',
      motifLevel: 'Motif {{level}}',
      personalBest: 'Rekor pribadi',
      record: 'Rekor {{score}}',
      canvasAria: 'Area menggambar motif {{name}}',
      resultExcellent: 'Canting Adiluhung',
      resultPatient: 'Tangan Telaten',
      resultGrowing: 'Jejak yang Tumbuh',
      resultStart: 'Mulai dari Satu Garis',
      resultGreatBody: 'Garis dan ritmemu cukup konsisten. Motif digital tersimpan di koleksi.',
      resultStartBody: 'Setiap pembatik memulai dari latihan. Coba ulangi dengan gerak lebih perlahan.',
      press: 'Tekan dan ikuti garis',
      slow: 'Gerakkan perlahan untuk skor lebih baik',
      clear: 'Hapus',
      retry: 'Ulangi',
      score: 'Nilai Jejakku',
      philosophy: 'Filosofi motif',
      weekly: 'Tantangan mingguan',
      time: '4h 18j',
      weeklyTitle: 'Jejak Sidomukti',
      weeklyBody: 'Capai skor 85 untuk membuka cap emas Laweyan dan bonus 350 XP.',
      result: 'Hasil jejakmu',
      collectionLink: 'Lihat koleksi',
      scoring: 'Cara penilaian',
      accuracy: 'Ketepatan garis',
      coverage: 'Cakupan motif',
      flow: 'Kelancaran gerak',
      keeper: 'Penjaga Motif',
      all: 'Semua',
    },
    categories: {
      heritage: 'Warisan',
      culinary: 'Kuliner',
      batik: 'Batik',
      spiritual: 'Spiritual',
      museum: 'Museum',
      green: 'Ruang hijau',
      creative: 'Kreatif',
      night: 'Malam',
      water: 'Air',
      city: 'Kota',
    },
    map: {
      loading: 'Membangun miniatur Solo…',
      dataUnavailable: 'Data peta belum tersedia.',
      all: 'Semua',
      amerta3d: 'Amerta 3D',
      culturalMiniature: 'Miniatur budaya',
      informativeMap: 'Peta informatif',
      nightMode: 'Malam',
      cityLights: 'Cahaya kota',
      adventure: 'Peta Petualangan Solo',
      summary: '14 landmark · sumbu warisan · bangunan 3D',
      explore: 'Jelajah kota',
      title: 'Solo sebagai dunia yang bisa dimainkan.',
      openQuest: 'Buka quest',
      searchPlaceholder: 'Cari landmark, cerita, kawasan…',
      clearSearch: 'Hapus pencarian',
      noResult: 'Tidak ada titik yang cocok.',
      featuredQuest: 'Quest Pilihan',
      mapStyle: 'Gaya peta',
      openImagery: 'Citra Terbuka',
      aerial: 'OpenAerialMap',
      recenter: 'Pusatkan',
      tour: 'Tur Sinematik',
      stopTour: 'Hentikan',
      motifGame: 'Game Motif',
      ranking: 'Peringkat',
      axis: 'Garis bercahaya menunjukkan Sumbu Warisan Solo.',
      save: 'Simpan lokasi',
      duration: 'durasi',
      access: 'akses',
      bestTime: 'waktu terbaik',
      locationQuest: 'Quest lokasi',
      reward: 'Hadiah: {{reward}} · +{{xp}} XP',
      exploreXp: 'Jelajahi · +{{xp}} XP',
      explored: 'Sudah Dijelajahi',
      addTrip: 'Tambahkan ke perjalanan',
      addedTrip: 'Sudah di perjalanan',
      playMotif: 'Mainkan Motif',
      etiquette: 'Etika kunjungan & data detail',
      accessibility: 'Aksesibilitas',
      disclaimer: 'Data demonstrasi MVP. Jam, tiket, dan kondisi akses perlu diverifikasi melalui pengelola resmi.',
      bright: 'Terang',
      dark: 'Gelap',
      satellite: 'Satelit',
    },
  },
};

const en = {
  translation: {
    language: {
      label: 'Language',
      id: 'Bahasa Indonesia',
      en: 'English',
      shortId: 'ID',
      shortEn: 'EN',
    },
    common: {
      home: 'Home',
      explore: 'Explore',
      learnSolo: 'Discover Solo',
      districts: 'Districts',
      travelGuide: 'Travel Guide',
      culinary: 'Taste of Sala',
      stories: 'City Stories',
      today: 'Events',
      play: 'Play',
      supportLocal: 'Support Local',
      passport: 'Passport',
      map3d: '3D Map',
      view360: 'Solo 360°',
      search: 'Search',
      close: 'Close',
      open: 'Open',
      viewAll: 'View all',
      viewDetails: 'View details',
      backHome: 'Back to the AmertaSala homepage',
      mainNavigation: 'Main navigation',
      cityNavigation: 'City brand navigation',
      searchCity: 'Search city information',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      exploreSolo: 'Explore Solo',
      loading: 'Loading…',
      loadError: 'The content could not be loaded.',
      demoData: 'MVP demonstration data',
      all: 'All',
      saved: 'Saved',
      remove: 'Remove',
      retry: 'Try again',
      level: 'Level {{level}} · {{xp}} XP',
    },
    header: { map: '3D Map' },
    footer: {
      intro: 'Solo’s digital gateway for understanding the city, planning a journey, hearing local voices, and experiencing heritage through interactive technology.',
      exploreMap: 'Explore the map',
      playBatik: 'Play with batik',
      explore: 'Explore',
      interact: 'Interact',
      brandPlatform: 'Brand Platform',
      motifGame: 'Motif Game',
      digitalPassport: 'Digital Passport',
      methodology: 'Methodology',
      prototype: 'Nusantara Digital City prototype · programme, event, and impact data are demonstrations for MVP purposes.',
    },
    landing: {
      eyebrow: 'AMERTASALA · WHERE HERITAGE LIVES FORWARD',
      heroTitle1: 'Solo, where',
      heroTitle2: 'heritage lives forward.',
      heroTitle3: '',
      heroBody: 'Enter Solo through its stories, flavours, neighbourhoods, and digital experiences—designed to help travellers understand the city, not merely see it.',
      heroAlt: 'Panorama of Solo at golden hour',
      mapAlt: 'Preview of Solo’s interactive map',
      philosophyAlt: 'Batik cloth overlooking the Solo skyline',
      culinaryAlt: 'Traditional Solo dishes',
      makerAlt: 'A Solo batik artisan',
      storyAlt: 'Portrait of a Solo resident',
      srawungAlt: 'A warm interaction between a resident and visitor in Solo',
      searchPlaceholder: 'Search places, flavours, stories, or experiences in Solo...',
      searchAria: 'Search Solo',
      find: 'Discover',
      quoteBefore: '“Come to see. ',
      quoteEmphasis: 'Srawung',
      quoteAfter: ' to understand.”',
      interactiveMap: 'Interactive map',
      openMap: 'Open',
      mapTitle: 'A map that feels alive, not just a collection of pins.',
      mapBody: 'Explore landmarks, districts, and cultural routes through an immersive 3D city experience.',
      upcoming: 'Upcoming events',
      philosophyEyebrow: 'The AmertaSala philosophy',
      philosophyTitle: 'Not a catalogue. Not a dashboard. A space to experience the soul of the city.',
      philosophyBody: 'AmertaSala presents Solo as a living experience. City information remains complete, but unfolds through a calmer, more human, and layered visual rhythm.',
      philosophy1: 'Clear and accessible city information',
      philosophy2: 'A practical digital travel guide',
      philosophy3: 'A more meaningful cultural experience',
      responsible: 'How to Srawung',
      districtEyebrow: 'Explore the districts',
      districtTitle: 'Choose a district. Discover a distinct side of Solo.',
      districtBody: 'Each choice reveals a different visual atmosphere, story, and rhythm of the city.',
      allDistricts: 'View all districts',
      viewMap: 'View on map',
      districtStory: 'Read the district story',
      journeyEyebrow: 'Digital travel guide',
      journeyTitle: 'Routes that guide you while leaving room to wander.',
      journeyBody: 'Choose a journey and watch its route unfold visually.',
      allRoutes: 'View all routes',
      addJourney: 'Add to My Journey',
      routeDetails: 'View route details',
      editorialEyebrow: 'Featured stories',
      editorialTitle: 'Editorial content that reveals itself as you explore.',
      culinaryFallbackTitle: 'Flavours that keep tradition alive',
      culinaryFallbackBody: 'Food stories presented with warmth and character.',
      makerFallbackTitle: 'Makers who sustain local craft',
      makerFallbackBody: 'Meet local makers and understand the process behind their work.',
      storyFallbackTitle: 'Stories carried by the people of the city',
      storyFallbackBody: 'Listen to the people who keep Solo’s pulse alive every day.',
      openCulinary: 'Open the culinary guide',
      meetMaker: 'Meet the maker',
      listenStory: 'Listen to the story',
      responsibleTitle: 'Interactive, yet warm and human.',
      responsibleBody: 'Motion guides attention rather than serving as decoration. Visitors can still read calmly and understand every message.',
      featureCity: 'A city information portal',
      featureResponsible: 'More responsible travel',
      featureCalm: 'A calmer and clearer experience',
      learnGuide: 'Read the guide',
      experienceEyebrow: 'Digital experiences',
      experienceTitle: 'Three ways to begin your journey.',
      mapCardBody: 'Explore Solo’s landmarks, districts, and urban layers.',
      batikTrace: 'Batik Trace',
      batikBody: 'Trace a motif and discover the philosophy behind it.',
      passportCollection: 'Passport & Collection',
      passportBody: 'Save your trails, stamps, and travel stories.',
      destinations: '{{count}} destinations',
      finalEyebrow: 'Ready to find your own Solo?',
      finalTitle: 'Solo is waiting to be disrawungi.',
      finalBody: 'Explore the city, play with its heritage, and take home a more meaningful story.',
      planJourney: 'Plan a journey',
      enteringMap: 'Entering the Solo 3D Map…',
      startExplore: 'Start Exploring',
      quickBatik: 'Batik',
      quickCulinary: 'Food',
      quickPalace: 'Palace',
      quickFamily: 'Family',
      quickAgenda: 'Events',
    },
    view360: {
      eyebrow: 'Virtual city exploration',
      title: 'Solo in 360°',
      title1: 'Step into the streets, spaces,',
      title2: 'and corners of Solo.',
      intro: 'Explore 40 locations across Solo and Greater Solo through interactive panoramas, listen to their stories, then continue to the map or your itinerary.',
      locations: 'locations',
      categories: 'categories',
      immersive: 'immersive experience',
      destinations: 'Featured destinations',
      sidebarTitle: 'Travel around Solo from your screen.',
      sidebarBody: 'Choose from {{count}} destinations, then click and drag the panorama to look around.',
      searchPlaceholder: 'Search places, areas, or categories…',
      choosePlace: 'Choose a place',
      noResults: 'No place found',
      noResultsBody: 'Try another keyword or category.',
      interactivePanorama: 'Interactive panorama',
      reload: 'Reload',
      fullscreen: 'Fullscreen',
      googleMaps: 'Google Maps',
      preparing: 'Preparing the panorama of {{name}}',
      wait: 'Please wait a moment…',
      frameTitle: '360-degree Street View of {{name}}',
      dragHint: 'Click and drag to explore in 360°',
      previous: 'Previous location',
      next: 'Next location',
      bestTime: 'Best time',
      position: 'Position',
      whyInteresting: 'Why it matters',
      openInMap: 'View on the 3D Map',
      continueJourney: 'Continue into the real journey',
      continueJourneyBody: 'Save this location or open a matching travel route.',
      audioTitle: 'Explore while listening',
      audioBody: 'Browser narration follows the active language. Choose ID or EN and listen to each place without constantly reading the screen.',
      narrationIntro: 'Welcome to Solo in three hundred and sixty degrees. Choose a place, move through the panorama, and listen to a short story before visiting it in person.',
      loading: 'Loading Solo 360°…',
      unavailable: 'Panorama data is currently unavailable.',
      landingEyebrow: 'Solo in 360°',
      landingTitle: 'Step inside Solo, even before you arrive.',
      landingBody: 'Enter heritage districts, markets, batik villages, public spaces, and transport hubs through 40 interactive panoramas.',
      landingCta: 'Explore Solo 360°',
      landingHint: 'Drag to look around',
      available: '{{count}} locations available',
      mapButton: 'View in 360°',
      itineraryButton: 'View in 360°',
    },
    profile: {
      loading: 'Loading the city profile…',
      error: 'The city profile could not be loaded.',
      eyebrow: 'Discover Solo',
      title1: 'Solo, a cultural city',
      title2: 'that keeps moving forward.',
      exploreMap: 'Explore the 3D map',
      planVisit: 'Plan your visit',
      glance: 'Solo at a glance',
      glanceTitle1: 'Essential city information,',
      glanceTitle2: 'clear and in context.',
      potential: 'Regional strengths',
      potentialTitle1: 'The strengths that make Solo',
      potentialTitle2: 'competitive and distinctive.',
      potentialBody: 'Each sector connects districts, local people, visitor experiences, and city stories.',
      timeline: 'City timeline',
      timelineTitle1: 'Heritage did not appear overnight.',
      timelineTitle2: 'It grew across generations.',
      ctaTitle: 'Discover information in its real location.',
      ctaBody: 'Open the 3D map to explore landmarks, districts, stories, visitor etiquette, and quests.',
      enterMap: 'Enter the map',
      panoramaAlt: 'Panorama of Solo',
    },
    districts: {
      error: 'The districts could not be loaded.',
      eyebrow: 'Explore by district',
      title1: 'Five districts.',
      title2: 'Five ways to read Solo.',
      intro: 'Rather than presenting Solo as a list of attractions, AmertaSala introduces the character, rhythm, and people shaping each district.',
      allMap: 'View them all on the 3D map',
      etiquette: 'How to srawung here',
      openMap: 'Open this district on the map',
      layers: 'One city, many layers',
      cta1: 'Find the district that best',
      cta2: 'matches your curiosity.',
      startMap: 'Start with the Solo map',
    },
    itineraries: {
      eyebrow: 'Digital travel guide',
      title1: 'Not only where to go.',
      title2: 'But how to experience it.',
      intro: 'Choose a journey by time, interests, travel style, local impact, and walking comfort.',
      heroAlt: 'A cultural journey through Solo',
      all: 'All',
      themed: 'Themed journeys',
      themedTitle1: 'Ready-made routes',
      themedTitle2: 'you can adapt.',
      culture: 'Culture',
      local: 'Local impact',
      walkability: 'Walkability',
      useRoute: 'Use this route',
      myJourney: 'My journey',
      board1: 'A journey board',
      board2: 'saved on this device.',
      addFromMap: 'Add from the map',
      empty: 'No locations yet. Add one from the map or use a suggested route.',
      summary: 'Journey summary',
      estimate: 'Estimated {{hours}} hours',
      locations: '{{count}} locations',
      storedDevice: 'Saved on this device',
      disclaimer: 'Route order and estimates are simulated for the MVP.',
      allLocations: 'View all locations',
      remove: 'Remove {{name}}',
    },
    culinary: {
      eyebrow: 'Taste of Sala',
      title1: 'A city can be understood',
      title2: 'through its flavours.',
      intro: 'Discover dishes, snacks, drinks, markets, and the people behind Solo’s food culture.',
      heroAlt: 'Food and daily life at Pasar Gede',
      nightAlt: 'Solo night food',
      addJourney: 'Add a flavour stop to your journey',
      guide: 'Digital culinary guide',
      guideTitle1: 'Choose by time,',
      guideTitle2: 'mood, and story.',
      searchPlaceholder: 'Search food, district, or mood…',
      vegetarian: 'Vegetarian',
      loading: 'Loading Taste of Sala…',
      all: 'All',
      noResults: 'No flavours match your current filters.',
      halal: 'Halal',
      saveTaste: 'Save this flavour',
      nightEyebrow: 'Solo after dark',
      nightTitle1: 'When the lights come on,',
      nightTitle2: 'the city’s flavour changes.',
      nightBody: 'Food stalls, warm drinks, grilled dishes, and late-night conversations are essential parts of experiencing Solo.',
      nightRoute: 'View the night route',
    },
    stories: {
      error: 'The city stories could not be loaded.',
      eyebrow: 'Voices from Sala',
      title1: 'A city is not built by buildings alone.',
      title2: 'A city is built by voices.',
      editorial: 'Editorial city brand',
      people1: 'The people who keep',
      people2: 'Solo alive.',
      pause: 'Pause story',
      listen: 'Listen to the story',
      playAria: 'Play the story of {{name}}',
      introBody: 'In production, this section becomes a space for interviews, audio recordings, documentary photography, and story archives curated together with residents.',
      archive: 'City archive',
      cta: 'One story can change how you see a place.',
      findDistrict: 'Find the district behind the story',
    },
    makers: {
      error: 'Made in Sala could not be loaded.',
      eyebrow: 'Made in Sala',
      title1: 'More than an object.',
      title2: 'It carries hands, time, and knowledge.',
      intro: 'Made in Sala turns local products into a city-branding medium—revealing their makers, processes, care instructions, and social impact.',
      heroAlt: 'A collection of batik and craft objects made by Solo artisans',
      meet: 'Meet the makers',
      transparency: 'Product transparency',
      transparencyTitle: 'Every work answers four questions.',
      who: 'Who made it?',
      whoBody: 'The people and communities behind the product.',
      how: 'How was it made?',
      howBody: 'The technique, materials, and time involved.',
      benefit: 'Who benefits?',
      benefitBody: 'How the product supports the local economy.',
      care: 'How should it be cared for?',
      careBody: 'So it lasts longer and does not quickly become waste.',
      since: '{{district}} · since {{since}}',
      process: 'Process',
      duration: 'Duration',
      careLabel: 'Care instructions',
    },
    responsible: {
      eyebrow: 'Responsible travel, the Solo way',
      title1: 'Come to see.',
      title2: 'Srawung to understand.',
      intro: 'How to Srawung is a simple guide for journeys that respect culture, residents’ living spaces, and local economic benefit.',
      heroAlt: 'A traveller interacting respectfully with a local trader at Pasar Gede',
      play: 'Learn through play',
      principles: '6 principles',
      guest: 'Tamu Srawung',
      phase: 'Before, during, and after your visit',
      phaseTitle1: 'Etiquette does not have to feel like a rulebook.',
      phaseTitle2: 'It can become part of the experience.',
      simulation: 'Micro simulation',
      simulationTitle1: 'Small choices,',
      simulationTitle2: 'different experiences.',
      quest: 'Find a quest on the map',
      quick: 'The quick choice',
      quickTitle: 'Arrive, take a photo, and leave.',
      knowledge: 'Knowledge',
      localBenefit: 'Local benefit',
      quickBody: 'Efficient, but the experience stops at the visual surface.',
      srawung: 'The Srawung choice',
      srawungTitle: 'Ask permission, start a conversation, and support local work.',
      simulatorBody: 'In the complete product, users face short situations—photographing a trader, buying batik, entering a cultural space—and see the impact of their choices without being judged.',
      principlesList: {
        listenTitle: 'Listen before judging',
        listenBody: 'Ask about the story, process, and context before treating a place as only a tourist object.',
        photoTitle: 'Ask before taking photographs',
        photoBody: 'Homes, markets, workshops, and places of worship are living spaces—not unlimited photo studios.',
        spendTitle: 'Let your spending stay longer',
        spendBody: 'Choose experiences, products, and services that directly benefit local people.',
        lightTitle: 'Move more lightly',
        lightBody: 'Reduce single-use waste, use water mindfully, and choose lighter short-distance travel.',
        srawungTitle: 'Srawung, not merely passing through',
        srawungBody: 'Make time to interact and understand the rhythm of the community you visit.',
        respectTitle: 'Respect cultural rules',
        respectBody: 'Follow guidance on clothing, access, photography, and behaviour in customary and religious spaces.',
      },
      srawungBody: 'Slower, but it leaves a relationship and a deeper understanding.',
    },
    today: {
      error: 'The event schedule could not be loaded.',
      intro: 'The city calendar connects travellers and residents with performances, workshops, district walks, community activities, and creative spaces.',
      openEvent: 'Open {{title}}',
      eyebrow: 'Solo Today',
      title1: 'Heritage is not only the past.',
      title2: 'It has a schedule today.',
      all: 'All',
      music: 'Music',
      textile: 'Textiles',
      food: 'Food',
      history: 'History',
      environment: 'Environment',
      demo: 'Demo data',
      ctaTitle: 'Events can become a reason to return to AmertaSala.',
      ctaBody: 'In production, schedules should connect to official sources and be updated regularly.',
      venueMap: 'Explore venues on the map',
    },
    search: {
      eyebrow: 'City search',
      title1: 'Search places, flavours,',
      title2: 'stories, or events.',
      placeholder: 'Example: batik, night food, green spaces…',
      button: 'Search',
      results: 'All results',
      map: 'Open the 3D map',
      details: 'View details',
      noResults: 'No matching results yet.',
      count: '{{count}} results found · MVP demonstration data',
      destination: 'Destination',
      district: 'District',
      culinary: 'Food',
      maker: 'Made in Sala',
      event: 'Event',
    },
    collection: {
      eyebrow: 'Digital passport',
      title: 'Every trail, motif, and story you take home.',
      republic: 'Republic of Sala Stories',
      explorerName: 'Explorer name',
      guest: 'Tamu Srawung',
      connector: 'Level {{level}} · Story Connector',
      current: 'Your journey so far',
      currentBody: 'A meaningful journey grows from small, mindful actions.',
      landmarks: 'landmarks',
      motifs: 'motifs',
      quests: 'quests',
      continue: 'Continue the challenge',
      gallery: 'Digital gallery',
      learned: 'Motifs you have learned',
      achievements: 'Achievements',
      badges: 'Stamps & Badges',
      first: 'First Explorer',
      firstBody: 'Open the Solo 3D map',
      five: 'Five Steps',
      fiveBody: 'Explore 5 landmarks',
      motifKeeper: 'Motif Keeper',
      motifKeeperBody: 'Earn a motif score of 85',
      srawungGuest: 'Tamu Srawung',
      srawungGuestBody: 'Complete 3 quests',
      savedLater: 'Saved for later',
      chosen: 'Your selected landmarks',
      openMap: 'Open the map',
      viewMap: 'View on map',
      empty: 'Save landmarks from the map to build your journey list.',
      unlockHint: 'Complete the challenge to unlock the motif philosophy.',
      bestScore: 'Best score {{score}}',
      locked: 'Locked',
    },
    leaderboard: {
      loading: 'Counting the explorers’ trails…',
      eyebrow: 'AmertaSala League',
      intro: 'A competition that celebrates learning, not merely winning.',
      season: 'Jejak Wastra Season',
      heroBody: 'Rankings are based on landmark exploration, cultural quests, and motif-tracing accuracy. The MVP data is simulated.',
      myJourney: 'Your journey',
      collectionMotif: 'Collected Motif',
      noMotif: 'Not selected yet',
      days: '{{count}} days',
      title1: 'Who is most committed to',
      title2: 'keeping the stories alive?',
      ends: 'Season ends in',
      time: '04 days 18 hours',
      reward: 'Digital reward: Golden Laweyan Stamp',
      weekly: 'Weekly ranking',
      keepers: 'Keepers of Motifs & Stories',
      global: 'Global',
      weeklyFilter: 'Weekly',
      solo: 'Solo',
      rank: 'Rank',
      explorer: 'Explorer',
      motif: 'Motif',
      streak: 'Streak',
      score: 'Score',
      healthy: 'A healthy leaderboard',
      healthyBody: 'There are no cash prizes or permanent rankings. Rankings reset regularly so new players still have a chance, while cultural learning remains the main goal.',
      reset: 'Reset demo',
    },
    game: {
      loading: 'Preparing the digital cloth…',
      eyebrow: 'Batik Trace Challenge',
      intro: 'Draw to understand, not merely to copy.',
      backMap: 'Back to the map',
      collection: 'Motif collection',
      choose: 'Choose a line you would like to learn.',
      chooseBody: 'Every pattern has its own rhythm and philosophy. Follow the path slowly, like the movement of a canting tool.',
      how: 'How to play & philosophy',
      notPerfect: 'This is not about having a perfect hand.',
      explanation: 'The game invites you to experience the precision, repetition, and patience involved in making batik.',
      motifLevel: '{{level}} motif',
      personalBest: 'Personal best',
      record: 'Best {{score}}',
      canvasAria: 'Drawing area for the {{name}} motif',
      resultExcellent: 'Masterful Canting',
      resultPatient: 'A Patient Hand',
      resultGrowing: 'A Growing Trace',
      resultStart: 'Begin with One Line',
      resultGreatBody: 'Your lines and rhythm are consistent. The digital motif has been saved to your collection.',
      resultStartBody: 'Every batik artisan begins with practice. Try again with a slower movement.',
      press: 'Press and follow the line',
      slow: 'Move slowly for a better score',
      clear: 'Clear',
      retry: 'Try again',
      score: 'Score My Trace',
      philosophy: 'Motif philosophy',
      weekly: 'Weekly challenge',
      time: '4h 18m',
      weeklyTitle: 'Sidomukti Trace',
      weeklyBody: 'Score 85 to unlock the Golden Laweyan stamp and 350 bonus XP.',
      result: 'Your trace result',
      collectionLink: 'View collection',
      scoring: 'How scoring works',
      accuracy: 'Line accuracy',
      coverage: 'Motif coverage',
      flow: 'Movement flow',
      keeper: 'Motif Keeper',
      all: 'All',
    },
    categories: {
      heritage: 'Heritage',
      culinary: 'Food',
      batik: 'Batik',
      spiritual: 'Spiritual',
      museum: 'Museum',
      green: 'Green spaces',
      creative: 'Creative',
      night: 'Night',
      water: 'Water',
      city: 'City',
    },
    map: {
      loading: 'Building a miniature Solo…',
      dataUnavailable: 'Map data is not available yet.',
      all: 'All',
      amerta3d: 'Amerta 3D',
      culturalMiniature: 'Cultural miniature',
      informativeMap: 'Informative map',
      nightMode: 'Night',
      cityLights: 'City lights',
      adventure: 'Solo Adventure Map',
      summary: '14 landmarks · heritage axis · 3D buildings',
      explore: 'Explore the city',
      title: 'Solo as a world you can play.',
      openQuest: 'Open quests',
      searchPlaceholder: 'Search landmarks, stories, districts…',
      clearSearch: 'Clear search',
      noResult: 'No matching places found.',
      featuredQuest: 'Featured Quest',
      mapStyle: 'Map style',
      openImagery: 'Open imagery',
      aerial: 'OpenAerialMap',
      recenter: 'Re-centre',
      tour: 'Cinematic Tour',
      stopTour: 'Stop',
      motifGame: 'Motif Game',
      ranking: 'Ranking',
      axis: 'The glowing line marks Solo’s Heritage Axis.',
      save: 'Save location',
      duration: 'duration',
      access: 'access',
      bestTime: 'best time',
      locationQuest: 'Location quest',
      reward: 'Reward: {{reward}} · +{{xp}} XP',
      exploreXp: 'Explore · +{{xp}} XP',
      explored: 'Explored',
      addTrip: 'Add to journey',
      addedTrip: 'Already in journey',
      playMotif: 'Play the Motif',
      etiquette: 'Visitor etiquette & details',
      accessibility: 'Accessibility',
      disclaimer: 'MVP demonstration data. Opening hours, tickets, and access conditions must be verified with official venue managers.',
      bright: 'Bright',
      dark: 'Dark',
      satellite: 'Satellite',
    },
  },
};


const narrationTranslations = {
  id: {
    listen: 'Dengarkan',
    listenPage: 'Dengarkan halaman',
    listenSection: 'Dengarkan bagian ini',
    listening: 'Sedang diputar',
    pause: 'Jeda narasi',
    resume: 'Lanjutkan narasi',
    stop: 'Hentikan narasi',
    player: 'Pemutar narasi AmertaSala',
    openPlayer: 'Buka pemutar narasi',
    nowPlaying: 'Sedang bercerita',
    paused: 'Narasi dijeda',
    ready: 'Siap didengarkan',
    voice: 'Suara pemandu',
    speed: 'Kecepatan',
    preview: 'Dengarkan contoh',
    noVoice: 'Browser belum menyediakan daftar suara. Coba buka kembali pemutar beberapa saat lagi.',
    previewText: 'Selamat datang di Amerta Sala. Mari mengenal Solo melalui budaya, rasa, dan cerita warganya.',
    tracks: {
      welcomeTitle: 'Selamat datang di AmertaSala',
      welcomeText: 'Selamat datang di Amerta Sala. Sebuah ruang digital untuk mengenal Solo, kota tempat warisan tidak hanya disimpan, tetapi terus hidup dalam keseharian masyarakatnya. Anda dapat menjelajahi kawasan, menemukan rasa, mendengar cerita warga, dan merencanakan perjalanan yang lebih bermakna.',
      philosophyTitle: 'Filosofi AmertaSala',
      philosophyText: 'Amerta Sala dirancang bukan sebagai katalog yang padat. Platform ini menjadi ruang untuk merasakan jiwa kota. Informasi disajikan dengan tenang dan berlapis, agar pengunjung dapat memahami Solo melalui budaya, kehidupan warga, dan nilai srawung.',
      districtsTitle: 'Mengenal kawasan Solo',
      districtsText: 'Setiap kawasan memiliki karakter yang berbeda. Laweyan dan Kauman menjaga tradisi batik. Pasar Gede hidup melalui perjumpaan dan rasa. Keraton dan Gladag menyimpan jejak sejarah kota. Pilih satu kawasan, lalu temukan ceritanya melalui peta interaktif.',
      journeyTitle: 'Panduan perjalanan',
      journeyText: 'Amerta Sala menyediakan rute perjalanan berdasarkan waktu, minat, dan gaya wisata. Gunakan rute sebagai titik awal. Beri ruang untuk berjalan perlahan, bertemu warga, mendukung pelaku lokal, dan menemukan pengalaman yang tidak selalu tercantum dalam daftar.',
      closingTitle: 'Solo menanti untuk disrawungi',
      closingText: 'Solo menanti untuk disrawungi. Jelajahi kota dengan rasa ingin tahu, hormati ruang hidup masyarakatnya, dan bawa pulang cerita yang lebih bermakna daripada sekadar foto perjalanan.',
    },
    routes: {
      profileTitle: 'Mengenal Kota Solo',
      profileText: 'Solo adalah kota budaya yang terus bergerak. Warisan keraton, kampung batik, pasar, ruang kreatif, dan kehidupan warganya saling terhubung. Halaman ini membantu Anda memahami identitas, potensi, dan perjalanan Solo dari masa ke masa.',
      districtsTitle: 'Jelajahi kawasan Solo',
      districtsText: 'Solo dapat dibaca melalui kawasan-kawasannya. Setiap kawasan memiliki ritme, karakter, karya, dan cara berinteraksi yang berbeda. Buka setiap cerita untuk menentukan kawasan yang paling sesuai dengan rasa ingin tahu Anda.',
      itineraryTitle: 'Panduan wisata digital',
      itineraryText: 'Pilih perjalanan berdasarkan waktu, minat, anggaran, dan kenyamanan berjalan kaki. Setiap rute dapat dimasukkan ke perjalanan Anda dan disesuaikan kembali melalui peta Solo tiga dimensi.',
      culinaryTitle: 'Rasa Sala',
      culinaryText: 'Kenali Solo melalui hidangan, jajanan, minuman, pasar, dan manusia di baliknya. Gunakan pencarian dan filter untuk menemukan rasa berdasarkan kawasan, suasana, serta kebutuhan perjalanan Anda.',
      storiesTitle: 'Suara Sala',
      storiesText: 'Kota tidak hanya dibentuk oleh bangunan. Dengarkan cerita dari warga, seniman, perajin, pedagang, dan penjaga tradisi yang membuat Solo terus hidup dari hari ke hari.',
      makersTitle: 'Made in Sala',
      makersText: 'Temui para pembuat karya lokal. Pelajari proses, waktu, keterampilan, dan dampak di balik setiap produk. Dukungan Anda membantu pengetahuan dan mata pencaharian lokal tetap tumbuh.',
      responsibleTitle: 'Cara Disrawungi',
      responsibleText: 'Menjadi tamu yang baik dimulai dari sikap sederhana. Minta izin sebelum memotret, hormati aturan tempat, jaga kebersihan, gunakan air dengan bijak, dan dukung usaha milik warga.',
      todayTitle: 'Solo hari ini',
      todayText: 'Temukan agenda, kegiatan budaya, pertunjukan, dan pengalaman kota yang berlangsung di Solo. Periksa kembali waktu dan lokasi kepada penyelenggara sebelum berangkat.',
      searchTitle: 'Pencarian AmertaSala',
      searchText: 'Gunakan pencarian untuk menemukan landmark, kawasan, kuliner, cerita, dan pengalaman di Solo. Masukkan kata kunci yang sederhana, lalu pilih hasil yang paling relevan.',
      collectionTitle: 'Paspor digital',
      collectionText: 'Paspor digital menyimpan jejak penjelajahan, lokasi pilihan, pencapaian, dan koleksi motif Anda pada perangkat ini. Lanjutkan perjalanan untuk membuka lebih banyak cap dan cerita.',
      leaderboardTitle: 'Papan peringkat',
      leaderboardText: 'Papan peringkat menampilkan pencapaian eksplorasi dan permainan motif. Gunakan fitur ini sebagai motivasi untuk belajar, bukan untuk terburu-buru melewati pengalaman kota.',
      gameTitle: 'Batik Trace',
      gameText: 'Ikuti garis motif batik secara perlahan. Permainan menilai ketepatan garis, cakupan motif, dan aliran gerakan. Setelah selesai, baca filosofi motif agar permainan juga menjadi pengalaman belajar.',
      mapTitle: 'Peta Solo tiga dimensi',
      mapText: 'Gunakan peta untuk menemukan landmark, kawasan, cerita, dan quest budaya. Pilih sebuah penanda untuk membuka detail lokasi. Anda juga dapat menjalankan tur sinematik atau menyimpan tempat ke perjalanan.',
    },
  },
  en: {
    listen: 'Listen',
    listenPage: 'Listen to this page',
    listenSection: 'Listen to this section',
    listening: 'Playing',
    pause: 'Pause narration',
    resume: 'Resume narration',
    stop: 'Stop narration',
    player: 'AmertaSala narration player',
    openPlayer: 'Open narration player',
    nowPlaying: 'Now telling',
    paused: 'Narration paused',
    ready: 'Ready to listen',
    voice: 'Guide voice',
    speed: 'Speed',
    preview: 'Preview voice',
    noVoice: 'The browser has not loaded its voice list yet. Open the player again in a moment.',
    previewText: 'Welcome to Amerta Sala. Discover Solo through its culture, flavours, and the stories of its people.',
    tracks: {
      welcomeTitle: 'Welcome to AmertaSala',
      welcomeText: 'Welcome to Amerta Sala, a digital gateway to Solo. Here, heritage is not merely preserved. It continues to live through everyday traditions, neighbourhoods, food, and the people of the city. Explore at your own pace and discover a more meaningful way to experience Solo.',
      philosophyTitle: 'The philosophy of AmertaSala',
      philosophyText: 'Amerta Sala is not designed as a crowded catalogue. It is a space for sensing the soul of the city. Information unfolds calmly and in layers, helping visitors understand Solo through culture, community life, and the Javanese spirit of srawung.',
      districtsTitle: 'Discover Solo by district',
      districtsText: 'Every district has a different character. Laweyan and Kauman sustain batik traditions. Pasar Gede lives through encounters and flavours. The Keraton and Gladag area carries the historical memory of the city. Choose a district, then follow its story through the interactive map.',
      journeyTitle: 'Your digital travel guide',
      journeyText: 'Amerta Sala offers journeys based on your time, interests, and travel style. Use each route as a starting point. Walk slowly, meet local people, support neighbourhood businesses, and leave room for discoveries beyond the itinerary.',
      closingTitle: 'Solo is ready to be experienced',
      closingText: 'Solo is ready to welcome you. Explore with curiosity, respect the living spaces of local communities, and take home stories that mean more than a collection of travel photographs.',
    },
    routes: {
      profileTitle: 'Meet the city of Solo',
      profileText: 'Solo is a cultural city that continues to move forward. Royal heritage, batik neighbourhoods, markets, creative spaces, and everyday community life are closely connected. This page introduces the identity, potential, and historical journey of the city.',
      districtsTitle: 'Explore Solo by district',
      districtsText: 'Solo can be understood through its districts. Each one has its own rhythm, character, crafts, and way of welcoming visitors. Open each story and choose the district that best matches your curiosity.',
      itineraryTitle: 'Digital travel guide',
      itineraryText: 'Choose a journey based on time, interests, budget, and walking comfort. Each route can be added to your personal journey and adjusted through the three D map of Solo.',
      culinaryTitle: 'The flavours of Solo',
      culinaryText: 'Discover Solo through dishes, snacks, drinks, markets, and the people behind its food culture. Search and filter by district, atmosphere, and travel needs.',
      storiesTitle: 'Voices of Solo',
      storiesText: 'A city is shaped by more than its buildings. Listen to residents, artists, artisans, traders, and cultural keepers who make Solo feel alive every day.',
      makersTitle: 'Made in Sala',
      makersText: 'Meet local makers and understand the process, time, skill, and impact behind their work. Your support helps local knowledge and livelihoods continue to grow.',
      responsibleTitle: 'How to be a thoughtful guest',
      responsibleText: 'Being a good guest begins with simple actions. Ask permission before taking photographs, respect local rules, keep places clean, use water responsibly, and support community-owned businesses.',
      todayTitle: 'Solo today',
      todayText: 'Find current cultural activities, performances, and city experiences in Solo. Confirm the latest time and location with the organiser before travelling.',
      searchTitle: 'Search AmertaSala',
      searchText: 'Use search to find landmarks, districts, food, stories, and experiences in Solo. Enter a simple keyword, then choose the result that is most relevant to your journey.',
      collectionTitle: 'Digital passport',
      collectionText: 'Your digital passport stores exploration history, saved places, achievements, and motif collections on this device. Continue your journey to unlock more stamps and stories.',
      leaderboardTitle: 'Leaderboard',
      leaderboardText: 'The leaderboard presents achievements from exploration and motif games. Use it as encouragement to learn, rather than a reason to rush through the city experience.',
      gameTitle: 'Batik Trace',
      gameText: 'Follow the batik motif slowly and carefully. The game measures line accuracy, motif coverage, and movement flow. When you finish, read the motif philosophy so that play also becomes a learning experience.',
      mapTitle: 'Three D map of Solo',
      mapText: 'Use the map to find landmarks, districts, stories, and cultural quests. Select a marker to open its details. You can also start a cinematic tour or save a place to your journey.',
    },
  },
} as const;

Object.assign(id.translation, { narration: narrationTranslations.id });
Object.assign(en.translation, { narration: narrationTranslations.en });

function resolveInitialLanguage(): AppLanguage {
  if (typeof window === 'undefined') return 'id';
  const stored = window.localStorage.getItem('amertasala-language');
  if (stored === 'id' || stored === 'en') return stored;
  return window.navigator.language.toLowerCase().startsWith('id') ? 'id' : 'en';
}

void i18n.use(initReactI18next).init({
  resources: { id, en },
  lng: resolveInitialLanguage(),
  fallbackLng: 'id',
  supportedLngs: [...supportedLanguages],
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

function syncLanguage(language: string) {
  const normalized: AppLanguage = language.startsWith('en') ? 'en' : 'id';
  if (typeof document !== 'undefined') document.documentElement.lang = normalized;
  if (typeof window !== 'undefined') window.localStorage.setItem('amertasala-language', normalized);
}

syncLanguage(i18n.language);
i18n.on('languageChanged', syncLanguage);

export default i18n;
